export interface FinanceDocumentDefinition {
  itemType: string;
  label: string;
  pluralLabel?: string;
  description: string;
  category: string;
  defaultPrefix: string;
  color: string;
}

export const CANONICAL_FINANCE_SEQUENCE_ORDER = [
  'invoice',
  'quote',
  'quote_request',
  'credit_note',
  'debit_note',
  'proforma',
  'sales_order',
  'delivery_note',
  'waybill',
  'work_order',
  'rma',
  'purchase_order',
  'grn',
  'supplier_credit',
  'supplier_quote',
  'rfq',
  'purchase_return',
  'expense',
  'picking_slip',
  'packing_slip',
  'stock_adjustment',
  'transfer_order',
] as const;

export type CanonicalFinanceItemType = typeof CANONICAL_FINANCE_SEQUENCE_ORDER[number];

export const FINANCE_DOCUMENT_DEFINITIONS: Record<CanonicalFinanceItemType, FinanceDocumentDefinition> = {
  invoice: { itemType: 'invoice', label: 'Invoice', pluralLabel: 'Invoices', description: 'Sales invoices', category: 'finance', defaultPrefix: 'INV', color: '#2563eb' },
  quote: { itemType: 'quote', label: 'Quote', pluralLabel: 'Quotes', description: 'Sales quotations', category: 'finance', defaultPrefix: 'QTE', color: '#7c3aed' },
  quote_request: { itemType: 'quote_request', label: 'Quote Request', pluralLabel: 'Quote Requests', description: 'Field quote requests awaiting pricing', category: 'finance', defaultPrefix: 'QR', color: '#d97706' },
  credit_note: { itemType: 'credit_note', label: 'Credit Note', pluralLabel: 'Credit Notes', description: 'Credit notes', category: 'finance', defaultPrefix: 'CN', color: '#059669' },
  debit_note: { itemType: 'debit_note', label: 'Debit Note', pluralLabel: 'Debit Notes', description: 'Debit notes', category: 'finance', defaultPrefix: 'DN', color: '#dc2626' },
  proforma: { itemType: 'proforma', label: 'Pro-Forma', pluralLabel: 'Pro-Forma Invoices', description: 'Pro-forma invoices', category: 'finance', defaultPrefix: 'PRO', color: '#0891b2' },
  sales_order: { itemType: 'sales_order', label: 'Sales Order', pluralLabel: 'Sales Orders', description: 'Sales orders', category: 'finance', defaultPrefix: 'SO', color: '#4f46e5' },
  delivery_note: { itemType: 'delivery_note', label: 'Delivery Note', pluralLabel: 'Delivery Notes', description: 'Delivery notes', category: 'finance', defaultPrefix: 'DN', color: '#ea580c' },
  waybill: { itemType: 'waybill', label: 'Waybill', pluralLabel: 'Waybills', description: 'Waybills and shipment consignment documents', category: 'finance', defaultPrefix: 'WB', color: '#f59e0b' },
  work_order: { itemType: 'work_order', label: 'Work Order', pluralLabel: 'Work Orders', description: 'Work orders', category: 'operations', defaultPrefix: 'WO', color: '#0f766e' },
  rma: { itemType: 'rma', label: 'RMA', pluralLabel: 'RMAs', description: 'Returned merchandise authorizations', category: 'finance', defaultPrefix: 'RMA', color: '#be123c' },
  purchase_order: { itemType: 'purchase_order', label: 'Purchase Order', pluralLabel: 'Purchase Orders', description: 'Purchase orders', category: 'finance', defaultPrefix: 'PO', color: '#15803d' },
  grn: { itemType: 'grn', label: 'Goods Received Note', pluralLabel: 'Goods Received Notes', description: 'Goods received notes', category: 'warehouse', defaultPrefix: 'GRN', color: '#0369a1' },
  supplier_credit: { itemType: 'supplier_credit', label: 'Supplier Credit', pluralLabel: 'Supplier Credits', description: 'Supplier credit notes', category: 'finance', defaultPrefix: 'SC', color: '#a16207' },
  supplier_quote: { itemType: 'supplier_quote', label: 'Supplier Quote', pluralLabel: 'Supplier Quotes', description: 'Supplier quotations', category: 'finance', defaultPrefix: 'SQ', color: '#6d28d9' },
  rfq: { itemType: 'rfq', label: 'RFQ', pluralLabel: 'RFQs', description: 'Requests for quote', category: 'finance', defaultPrefix: 'RFQ', color: '#1d4ed8' },
  purchase_return: { itemType: 'purchase_return', label: 'Purchase Return', pluralLabel: 'Purchase Returns', description: 'Purchase returns', category: 'finance', defaultPrefix: 'PR', color: '#b91c1c' },
  expense: { itemType: 'expense', label: 'Expense', pluralLabel: 'Expenses', description: 'Unified expenses (bills, fuel, toll, etc.)', category: 'finance', defaultPrefix: 'EXP', color: '#059669' },
  picking_slip: { itemType: 'picking_slip', label: 'Picking Slip', pluralLabel: 'Picking Slips', description: 'Warehouse picking slips', category: 'warehouse', defaultPrefix: 'PK', color: '#0f766e' },
  packing_slip: { itemType: 'packing_slip', label: 'Packing Slip', pluralLabel: 'Packing Slips', description: 'Warehouse packing slips', category: 'warehouse', defaultPrefix: 'PS', color: '#7c2d12' },
  stock_adjustment: { itemType: 'stock_adjustment', label: 'Stock Adjustment', pluralLabel: 'Stock Adjustments', description: 'Stock adjustment documents', category: 'warehouse', defaultPrefix: 'SA', color: '#4338ca' },
  transfer_order: { itemType: 'transfer_order', label: 'Transfer Order', pluralLabel: 'Transfer Orders', description: 'Inventory transfer orders', category: 'warehouse', defaultPrefix: 'TO', color: '#0d9488' },
};

export const OPERATIONS_FINANCIAL_SALES_ITEM_TYPES = [
  'invoice',
  'quote',
  'quote_request',
  'proforma',
  'sales_order',
  'delivery_note',
  'waybill',
  'credit_note',
] as const satisfies ReadonlyArray<CanonicalFinanceItemType>;

export const OPERATIONS_FINANCIAL_PURCHASE_ITEM_TYPES = [
  'purchase_order',
  'grn',
  'supplier_credit',
  'supplier_quote',
  'expense',
] as const satisfies ReadonlyArray<CanonicalFinanceItemType>;

export const OPERATIONS_FINANCIAL_DOCUMENT_TYPES = [
  ...OPERATIONS_FINANCIAL_SALES_ITEM_TYPES,
  ...OPERATIONS_FINANCIAL_PURCHASE_ITEM_TYPES,
] as const;

export function getFinanceDocumentDefinition(itemType: string): FinanceDocumentDefinition | null {
  return FINANCE_DOCUMENT_DEFINITIONS[itemType as CanonicalFinanceItemType] || null;
}
